import React, { useEffect, useState, useRef, useCallback } from 'react';
import ChatAPI, { Message as MessageModel, MessageType } from '../../chat-api';
import { Container, LoadingContainer, MessagesContainer } from './styles';
import { getMessagesByDate, isLastMessage, isMsgOut } from '../../utils/index';
import TimeDivider from './TimeDivider/TimeDivider';
import Message from "./Message/Message";
import { useMessageState, useChatState, useTheme, useAppState } from '../../data';
import CustomKeyboard from './CustomKeyboard/CustomKeyboard';

export interface ChatBodyProps {
  backgroundColor?: string;
  timeDividerBackgroundColor?: string;
  timeDividerTextColor?: string;
  buttonsColor?: string;
  customer?: {
    messageBackgroundColor?: string;
    messageTextColor?: string;
    messageHourTextColor?: string;
    runnableTrackColor?: string;
    sliderTrackColor?: string;
  };
  enterprise?: {
    messageBackgroundColor?: string;
    messageTextColor?: string;
    messageHourTextColor?: string;
    runnableTrackColor?: string;
    sliderTrackColor?: string;
  };
  responsiveMode?: boolean;
}

const ChatBody: React.FC<ChatBodyProps> = () => {
  const { timeDividerBackgroundColor, timeDividerTextColor, backgroundColor } = useTheme().body!;
  const chatAPI = new ChatAPI();
  const { messages, setMessages, scrollBottom, setScrollBottom, sendingMessages } = useMessageState();
  const { chat, isExpanded } = useChatState();
  const { initialMessage, communicationTool } = useAppState();
  const [isLoadingOldMessages, setIsLoadingOldMessages] = useState<boolean>(false);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const [newChat, setNewChat] = useState<boolean>(false);

  useEffect(() => {
    const removeFirstLoading = () => {
      let newMessages = JSON.parse(JSON.stringify(messages));
      let index = (newMessages || []).findIndex((msg: any) => msg.loading);
      if (index !== -1) {
        newMessages[index].loading = false;
        setMessages(newMessages);
      }
    }

    let interval = setInterval(removeFirstLoading, 1000);

    return () => {
      clearInterval(interval);
    }
  });

  useEffect(() => {
    const callback = (messagesContainer: { messages: MessageModel[] }) => {
      let newMessages = JSON.parse(JSON.stringify(messagesContainer.messages));
      if (messages.length !== newMessages.length) {
        newMessages = newMessages.map((msg: any) => { msg.loading = false; return msg; });
        for (let i = newMessages.length - 1; i >= messages?.length; i--) {
          let isOut = isMsgOut(newMessages[i], chat?.communicationTool || communicationTool);
          if (!isOut)
            break;
          newMessages[i].loading = true;
        }
      }

      setMessages(newMessages);

      if (chatRef.current?.scrollTop! + chatRef.current?.offsetHeight! === chatRef.current?.scrollHeight) {
        footerRef?.current?.scrollIntoView({
          block: 'start',
        });
      }
    };

    chatAPI.on('messages', callback);
    return () => {
      chatAPI.off('messages', callback);
    };
  }, [chatAPI, setMessages, messages, chat, communicationTool]);

  useEffect(() => {
    if (chat) {
      setHasMoreMessages(!newChat);
    } else {
      setNewChat(true);
      if (initialMessage) {
        chatAPI.addInitialMessage(initialMessage, communicationTool);
      }
    }
  }, [chat, communicationTool, initialMessage, chatAPI, newChat]);

  const handleScroll = useCallback(() => {
    function loadMoreMessages() {
      setIsLoadingOldMessages(true);
      try {
        const oldScrollTop = chatRef.current?.scrollTop;
        chatAPI.getOldMessages().then(data => {
          if (chatRef.current?.scrollTop === 0) {
            chatRef.current?.scrollTo(0, Number(oldScrollTop) + 10);
          }
          setHasMoreMessages(data.messages.length > 0);
          setIsLoadingOldMessages(false);
        });
      } catch (error) {
        setIsLoadingOldMessages(false);
      }
    }

    if (chatRef.current) {
      if (chatRef.current.scrollTop > 0 && chatRef.current.scrollTop < 100 && !isLoadingOldMessages && hasMoreMessages) {
        loadMoreMessages();
      } else if (chatRef.current.scrollTop === 0 && hasMoreMessages && !isLoadingOldMessages) {
        chatRef.current?.scrollTo(0, 50);
      }
    }

    if (!(chatRef.current?.scrollTop! + chatRef.current?.offsetHeight! > chatRef.current?.scrollHeight! - 100)) {
      setScrollBottom(false);
    } else {
      setScrollBottom(true);
    }
  }, [hasMoreMessages, isLoadingOldMessages, chatAPI, setScrollBottom]);

  useEffect(() => {
    function loadMoreMessages() {
      setIsLoadingOldMessages(true);
      try {
        chatAPI.getOldMessages().then((data: any) => {
          if (scrollBottom) {
            footerRef?.current?.scrollIntoView({
              block: 'start',
            });
          }
          setHasMoreMessages(data.messages.length > 0);
          setIsLoadingOldMessages(false);
        });
      } catch (error) {
        setIsLoadingOldMessages(false);
      }
    }

    const messagesHeight = messagesRef?.current?.offsetHeight || 0;
    const chatHeight = chatRef?.current?.offsetHeight || 0;

    if (chat && messagesHeight !== 0 && messagesHeight < chatHeight && hasMoreMessages && !isLoadingOldMessages) {
      loadMoreMessages();
    }

    if (footerRef.current && messages.length > 0 && scrollBottom) {
      footerRef.current.scrollIntoView({
        block: 'start',
      });
    }
  }, [messages, scrollBottom, hasMoreMessages, isLoadingOldMessages, chat, chatAPI, sendingMessages]);

  const sortedMessages = (messages || []).sort((msgA, msgB) => {
    return (new Date(msgA.date).getTime()) - (new Date(msgB.date).getTime());
  });

  const isLastLoading = (message: any) => {
    let index = messages.findIndex((msg) => message._id === msg._id);
    if (index === -1 || !message.loading)
      return false;
    if (index === messages.length - 1 || (index + 1 < messages.length && !messages[index + 1].loading))
      return true;

    return false;
  }

  const allowedMessages = (messages || []).filter((msg: any) => msg.type !== MessageType.Internal && (!msg.loading || isLastLoading(msg))).sort((msgA, msgB) => {
    return (new Date(msgA.date).getTime()) - (new Date(msgB.date).getTime());
  });

  const canAnimate = (message: any): Boolean => {
    return (isMsgOut(message, chat?.communicationTool || communicationTool) || (sendingMessages || []).findIndex(el => el._id === message._id) !== -1)
  }
  return (
    <Container
      as="sdiv"
      styles={{ backgroundColor }}
      ref={chatRef}
      onScroll={handleScroll}
      isExpanded={isExpanded}
    >
      <MessagesContainer as="sdiv" ref={messagesRef}>
        {(isLoadingOldMessages || (hasMoreMessages && messages.length === 0)) && (
          <LoadingContainer as="sdiv">
            <svg className={'wppMessageLoading'} width="24" height="24" viewBox="0 0 43 43">
              <circle className={'wppMessageLoadingCircle'} cx="21.5" cy="21.5" r="18" fill="none" strokeWidth="6" />
            </svg>
          </LoadingContainer>
        )}
        {Object.values(getMessagesByDate(allowedMessages, sendingMessages)).map((messagesByDate: MessageModel[]) => (
          <sdiv key={messagesByDate[0]._id}>
            <TimeDivider date={messagesByDate[0].date} styles={{ timeDividerBackgroundColor, timeDividerTextColor }} />
            {messagesByDate.map((message: MessageModel, i: number) => (
              <Message
                key={message._id}
                message={message}
                lastChild={isLastMessage(i, messagesByDate, message)}
                index={i}
                animate={canAnimate(message)}
              />
            ))}
          </sdiv>
        ))}
        <sdiv ref={footerRef} />
      </MessagesContainer>
      {
        (((!sendingMessages || sendingMessages.length === 0) && sortedMessages && sortedMessages.length && !sortedMessages[sortedMessages.length - 1].loading && sortedMessages[sortedMessages.length - 1].customKeyboards) || [])
          .map((keyboard, index) => (
            <CustomKeyboard
              buttons={keyboard.buttons}
              key={index}
            />))
      }
    </Container>
  );
}

export default ChatBody;